import React, {
    useEffect,
    useState
} from 'react';
import PropTypes from 'prop-types';

import {
    RIQPopover
} from './RIQPopover.jsx';

import {
    PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip
} from 'recharts';

const DEFAULT_PIE_CHART_HEIGHT = 182;
const OTHER_BUCKET_COLOR = '#777777';

function PieChartTooltip({active, expandedFormatter, hide = false, payload, percentageFormatter}) {

    const datum = payload?.[0];
    return (
        <>
            {datum && <div className='tlw-pointer-events-auto'>
                <RIQPopover
                    triggerNode={<div/>}
                    className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
                    placement={'top'}
                    additionalHookProps={{
                        visible: (active && !hide)
                    }}
                >
                    <div>
                        <div className='tlw-m-0 tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-px-7 tlw-pt-4 tlw-text-md tlw-text-gray-700'>
                            <svg
                                width='11px'
                                height='11px'
                                style={{
                                    background: datum.payload.fill
                                }}
                                className='tlw-rounded-sm'
                            >
                            </svg>
                            &nbsp;{datum.name}
                        </div>
                        <div className='tlw-px-6 tlw-text-left'>
                            <div
                                className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                {expandedFormatter?.(datum.value)}
                            </div>
                        </div>
                        <div className='tlw-px-6 tlw-pb-4.5 tlw-text-left'>
                            <div
                                className='tlw-whitespace-nowrap tlw-text-md tlw-text-gray-700'>
                                ({percentageFormatter?.(datum.payload.totalPercentage)} of total)
                            </div>
                        </div>
                    </div>
                </RIQPopover>
            </div>}
        </>
    );
}

function _MetricPieChart({pieChartHeight, data, chartConfig, tooltipPlacement, onClick}) {

    const {
        expandedFormatter,
        percentageFormatter,
        index,
        metricName,
        dataKey,
        pieColors,
        abbreviatedFormatter,
        innerRadius = 40,
        outerRadius = 80
    } = chartConfig;
    const [tooltipPoint, setTooltipPoint] = useState();
    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

    // NOTE we need to keep track of global mouse movement so that we can hide
    // the pie slice tooltips when they should not be displayed. Recharts does
    // not do a great job of tracking this state itself, at least with a custom
    // tooltip like we use, so we must do so ourselves.
    useEffect(() => {
        const onMouseMove = (e) => {
            setShouldShowTooltip(shouldShowTooltip => {
                const element = document.elementFromPoint(e.clientX, e.clientY);
                return (element && (element.classList.contains('recharts-sector') || element.closest('.riq-popover-container')));
            });
        };

        window.addEventListener('mousemove', onMouseMove);

        return () => window.removeEventListener('mousemove', onMouseMove);
    }, []);

    function onMouseMove(e) {
        if (e) {
            setTooltipPoint(e.tooltipPosition);
        }
    }
    const tooltip = (
        <PieChartTooltip
            hide={!shouldShowTooltip}
            expandedFormatter={expandedFormatter}
            percentageFormatter={percentageFormatter}
            placement={tooltipPlacement || 'top'}
        />
    );

    function onItemClick(datum) {
        onClick && onClick(datum.item);
    }

    const className = index > 0 ? 'tlw-w-full tlw-h-full tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300 tlw-p-7.5' : 'tlw-h-full tlw-p-7.5';

    let header = null;
    if (metricName) {
        header = (
            <h4 className='tlw-text-11 tlw-font-semibold tlw-uppercase tlw-text-gray-700'>
                {metricName}
            </h4>
        );
    }

    return (
        <div
            key={metricName}
            className={className}>
            {header}
            <div className='tlw-flex tlw-h-full tlw-flex-row'>
                <div className='tlw-flex tlw-w-4/6 tlw-items-center'>
                    <ResponsiveContainer
                        width='100%'
                        height={pieChartHeight}>
                        <PieChart>
                            <Pie
                                dataKey={dataKey}
                                data={data}
                                startAngle={90}
                                endAngle={-450}
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                                onMouseMove={onMouseMove}
                            >
                                {data.map((item, index) => (
                                    <Cell
                                        className={onClick ? 'tlw-cursor-pointer' : ''}
                                        key={`cell-${index}`}
                                        fill={item.name !== 'Other' ? pieColors?.(item, index) : OTHER_BUCKET_COLOR}
                                        onClick={() => onItemClick({
                                            item
                                        })}
                                    />
                                ))}
                            </Pie>
                            <Tooltip
                                content={tooltip}
                                position={tooltipPoint}
                                wrapperStyle={{
                                    // an ongoing bug for Recharts is looking to make the "wrapperStyle" of this unnecessary https://github.com/recharts/recharts/issues/2920
                                    outline: 'none'
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className='tlw-relative tlw-flex tlw-w-2/6'>
                    <Legend
                        layout='vertical'
                        verticalAlign='top'
                        align='right'
                        width='100%'
                        iconSize='11'
                        wrapperStyle={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center'
                        }}
                        payload={
                            data.map(
                                (item, index) => ({
                                    id: `item-${index}`,
                                    type: 'square',
                                    value: _.concat(`${item.name}: `, abbreviatedFormatter?.(item.value)),
                                    color: item.name !== 'Other' ? pieColors?.(item, index) : OTHER_BUCKET_COLOR,
                                    item: item
                                })
                            )
                        }
                        formatter={(value) => <span className={`tlw-mb-5 tlw-inline-block tlw-text-sm tlw-text-gray-900 ${onClick ? 'tlw-cursor-pointer' : ''}`}>{value[0]}<strong>{value[1]}</strong></span>}
                        onClick={onItemClick}
                    />
                </div>
            </div>
        </div>
    );
}


RIQMetricPieChart.propTypes = {
    pieChartHeight: PropTypes.number,
    dataByInterval: PropTypes.arrayOf(PropTypes.array).isRequired,
    chartConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClick: PropTypes.func
};

export default function RIQMetricPieChart({pieChartHeight, dataByInterval, chartConfigs, onClick}) {

    pieChartHeight ||= DEFAULT_PIE_CHART_HEIGHT;

    return (
        <>
            {_.map(chartConfigs, (chartConfig, idx) => {
                let data = dataByInterval[idx];
                const limit = chartConfig.sectionLimit;
                if (limit && data.length > limit) {
                    data = [...data.slice(0, limit), {
                        name: 'Other',
                        value: data.slice(limit).reduce((tot, ele) => tot + Number(ele.value), 0)
                    }];
                }
                chartConfig.index = idx;
                return (
                    <_MetricPieChart
                        key={idx}
                        pieChartHeight={pieChartHeight}
                        data={data}
                        chartConfig={chartConfig}
                        tooltipPlacement={'top'}
                        onClick={item => onClick && onClick(item, chartConfig)}
                    />
                );
            })}
        </>
    );
}
